import { SelectionModel } from '@angular/cdk/collections';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  IotEdgeReleaseResponse,
  IotEdgeReleaseService,
  SiteReleasesResponse,
} from '../services/iotEdgeRelease.service';
import { Site, SiteService, SiteState } from '../services/site.service';

function compareVersionsDesc(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pb[i] || 0) - (pa[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

@Component({
  selector: 'app-sites',
  templateUrl: './sites.component.html',
  styleUrls: ['./sites.component.css'],
  standalone: false,
})
export class SitesComponent implements OnInit {
  sitesTableSource: MatTableDataSource<Site> = new MatTableDataSource();
  sites: Site[];
  allReleases: IotEdgeReleaseResponse[] = [];
  siteReleasesMap: { [siteId: string]: SiteReleasesResponse } = {};
  siteAutoUpdateMap: { [siteId: string]: boolean } = {};
  settingLatestAvailable = false;
  public sortActive: string = 'name';
  public sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private siteService: SiteService,
    private releaseService: IotEdgeReleaseService,
    public dialog: MatDialog,
    public snackBar: MatSnackBar
  ) {}

  displayedColumns = [
    'select',
    'id',
    'customer',
    'name',
    'state',
    'deploymentStatus',
    'onPremStatus',
    'currentRelease',
    'desiredRelease',
    'latestAvailable',
    'isAutoUpdate',
    'connectPOB',
    'gangway',
    'connectFuel',
    'cargoConditionMonitoring',
    'cabinBunkManagement',
    'hvac',
    'comment',
  ];

  filters = {
    prod: true,
    pending: true,
    dev: false,
    discontinued: false,
    demo: false,
    searchString: '',
    customer: '' as string,
    currentRelease: '' as string,
    desiredRelease: '' as string,
    deploymentStatus: '' as string,
  };

  get uniqueCustomers(): string[] {
    if (!this.sites) return [];
    const names = [...new Set(this.sites.map((s) => s.customer?.name).filter(Boolean))];
    return names.sort();
  }

  get uniqueReleaseVersions(): string[] {
    return this.allReleases.map((r) => r.version);
  }

  ngOnInit() {
    this.fetchSites();
    this.fetchAllReleases();
  }

  @ViewChild(MatSort) set sort(sort: MatSort) {
    if (sort) {
      this.sitesTableSource.sort = sort;
      sort.active = this.sortActive;
      sort.direction = this.sortDirection;
      this.sitesTableSource.sortingDataAccessor = (item, property) => {
        switch (property) {
          case 'id':
            return item.id || '';
          case 'customer':
            return item.customer?.name || '';
          case 'name':
            return item.name || '';
          case 'state':
            return item.state || '';
          case 'comment':
            return item.comment || '';
          case 'connectPOB':
            return item.featureFlags?.connectPOB ? 1 : 0;
          case 'gangway':
            return item.featureFlags?.gangway ? 1 : 0;
          case 'connectFuel':
            return item.featureFlags?.connectFuel ? 1 : 0;
          case 'cargoConditionMonitoring':
            return item.featureFlags?.cargoConditionMonitoring ? 1 : 0;
          case 'cabinBunkManagement':
            return item.featureFlags?.cabinBunkManagement ? 1 : 0;
          case 'hvac':
            return item.featureFlags?.hvac ? 1 : 0;
          case 'currentRelease':
            return this.siteReleasesMap[item.id]?.currentRelease?.version || '';
          case 'desiredRelease':
            return this.siteReleasesMap[item.id]?.desiredRelease?.version || '';
          case 'latestAvailable':
            return this.getLatestAvailableVersion(item);
          case 'isAutoUpdate':
            return this.isAutoUpdateEnabled(item) ? 1 : 0;
          case 'deploymentStatus':
            return this.siteReleasesMap[item.id]?.deploymentStatus ?? 0;
          default:
            return item[property] ?? '';
        }
      };
    }
  }

  onSortChange(event) {
    this.sortActive = event.active;
    this.sortDirection = event.direction;
  }

  filterSites() {
    this.sitesTableSource.data = this.sites.filter(
      (s) =>
        ((this.filters.prod && s.state == SiteState.production) ||
          (this.filters.pending && s.state == SiteState.pending) ||
          (this.filters.dev && s.state == SiteState.development) ||
          (this.filters.demo && s.state == SiteState.demo) ||
          (this.filters.discontinued && s.state == SiteState.discontinued)) &&
        (this.filters.searchString == '' ||
          s.name.toLowerCase().includes(this.filters.searchString.toLowerCase()) ||
          s.state.toLowerCase().includes(this.filters.searchString.toLowerCase()) ||
          s.customer.name.toLowerCase().includes(this.filters.searchString.toLowerCase())) &&
        (!this.filters.customer || s.customer?.name === this.filters.customer) &&
        (!this.filters.currentRelease ||
          this.siteReleasesMap[s.id]?.currentRelease?.version === this.filters.currentRelease) &&
        (!this.filters.desiredRelease ||
          this.siteReleasesMap[s.id]?.desiredRelease?.version === this.filters.desiredRelease) &&
        (!this.filters.deploymentStatus || this.matchesDeploymentFilter(this.siteReleasesMap[s.id]?.deploymentStatus))
    );
  }

  private matchesDeploymentFilter(status: any): boolean {
    const filter = this.filters.deploymentStatus;
    if (!filter) return true;
    // Handle both numeric and string enum values from the API
    const statusMap = { '0': 'Unknown', '1': 'Healthy', '2': 'ReportingFailure', '3': 'ContainerMismatch' };
    const numericMap = { Unknown: '0', Healthy: '1', ReportingFailure: '2', ContainerMismatch: '3' };
    const statusStr = String(status ?? 0);
    return statusStr === filter || statusMap[filter] === statusStr || numericMap[statusStr] === filter;
  }

  public selected = new SelectionModel<Site>(true, []);
  isAllSelected() {
    const numSelected = this.selected.selected.length;
    const numRows = this.sitesTableSource.data.length;
    return numSelected === numRows;
  }
  masterToggle() {
    if (this.isAllSelected()) {
      this.selected.clear();
    } else {
      this.sitesTableSource.data.forEach((hd) => this.selected.select(hd));
    }
  }

  fetchSites(): void {
    this.siteService.getSites().subscribe(
      (s) => {
        this.sites = s;
        this.sitesTableSource.data = s;
        this.filterSites();
        this.fetchSiteReleases(s);
        this.fetchSiteSettings(s);
      },
      (err: HttpErrorResponse) => {
        this.snackBar.open(`There was an error fetching sites ${err?.message ?? err}`, null, {
          duration: 10000,
        });
        console.error(err);
      }
    );
  }

  fetchAllReleases(): void {
    this.releaseService.getAllReleases().subscribe(
      (releases) => {
        this.allReleases = releases.sort((a, b) => compareVersionsDesc(a.version, b.version));
      },
      (err) => console.error('Error fetching all releases', err)
    );
  }

  fetchSiteReleases(sites: Site[]): void {
    sites.forEach((site) => {
      this.releaseService.getSiteReleases(site.id).subscribe(
        (sr) => {
          this.siteReleasesMap[site.id] = sr;
          console.log(`Site ${site.name} deploymentStatus:`, sr.deploymentStatus, typeof sr.deploymentStatus);
        },
        () => {} // silently ignore per-site errors
      );
    });
  }

  fetchSiteSettings(sites: Site[]): void {
    sites.forEach((site) => {
      this.siteService.getSettings(site.id).subscribe(
        (settings) => {
          const setting = settings.find((s) => s.key === 'ReleaseAutoDeployEnabled');
          this.siteAutoUpdateMap[site.id] = setting?.value?.trim().toLowerCase() === 'true';
        },
        () => {} // silently ignore per-site errors; defaults to false
      );
    });
  }

  isAutoUpdateEnabled(site: Site): boolean {
    return this.siteAutoUpdateMap[site.id] ?? false;
  }

  get latestRelease(): IotEdgeReleaseResponse | null {
    return this.allReleases.length > 0 ? this.allReleases[0] : null;
  }

  getCurrentVersion(site: Site): string {
    return this.siteReleasesMap[site.id]?.currentRelease?.version || '—';
  }

  getDesiredVersion(site: Site): string {
    return this.siteReleasesMap[site.id]?.desiredRelease?.version || '—';
  }

  getLatestAvailableVersion(site: Site): string {
    const releases = this.siteReleasesMap[site.id]?.availableReleases;
    if (!releases || releases.length === 0) return '—';
    return [...releases].sort((a, b) => compareVersionsDesc(a.version, b.version))[0].version;
  }

  getLatestAvailableReleaseObj(site: Site): IotEdgeReleaseResponse | null {
    const releases = this.siteReleasesMap[site.id]?.availableReleases;
    if (!releases || releases.length === 0) return null;
    const latestId = [...releases].sort((a, b) => compareVersionsDesc(a.version, b.version))[0].id;
    return this.allReleases.find((r) => r.id === latestId) || null;
  }

  /** null = no latest available; true = ready; false = available but not ready */
  isLatestAvailableReady(site: Site): boolean | null {
    const rel = this.getLatestAvailableReleaseObj(site);
    if (!rel) return null;
    return (rel.readySiteIds ?? []).includes(site.id);
  }

  isOnLatest(site: Site): boolean {
    const current = this.siteReleasesMap[site.id]?.currentRelease;
    return !!current && !!this.latestRelease && current.id === this.latestRelease.id;
  }

  private normalizeDeploymentStatus(site: Site): string {
    const raw = this.siteReleasesMap[site.id]?.deploymentStatus;
    if (raw == null) return 'unknown';
    const s = String(raw).toLowerCase();
    if (s === '1' || s === 'healthy') return 'healthy';
    if (s === '2' || s === 'reportingfailure') return 'failure';
    if (s === '3' || s === 'containermismatch') return 'mismatch';
    return 'unknown';
  }

  isVersionPending(site: Site): boolean {
    const sr = this.siteReleasesMap[site.id];
    if (!sr?.currentRelease || !sr?.desiredRelease) return false;
    return sr.currentRelease.id !== sr.desiredRelease.id;
  }

  getDeploymentStatusLabel(site: Site): string {
    if (this.normalizeDeploymentStatus(site) === 'healthy' && this.isVersionPending(site)) return 'Update Pending';
    switch (this.normalizeDeploymentStatus(site)) {
      case 'healthy':
        return 'Healthy';
      case 'failure':
        return 'Failure';
      case 'mismatch':
        return 'Mismatch';
      default:
        return '—';
    }
  }

  getDeploymentStatusClass(site: Site): string {
    if (this.normalizeDeploymentStatus(site) === 'healthy' && this.isVersionPending(site)) return 'deploy-pending';
    switch (this.normalizeDeploymentStatus(site)) {
      case 'healthy':
        return 'deploy-healthy';
      case 'failure':
        return 'deploy-failure';
      case 'mismatch':
        return 'deploy-mismatch';
      default:
        return 'deploy-unknown';
    }
  }

  private parseUtcDate(isoString: string): number {
    // Server timestamps may lack a timezone suffix — append Z to force UTC parsing
    const s = /[Z+\-]\d{2}:?\d{2}$|Z$/.test(isoString) ? isoString : isoString + 'Z';
    return new Date(s).getTime();
  }

  private getOnPremNormalizedStatus(site: Site): string {
    const sr = this.siteReleasesMap[site.id];
    if (!sr) return 'unknown';
    // Check if report is outdated (older than 1 hour)
    if (sr.reportedContainersAt) {
      const reportedAt = this.parseUtcDate(sr.reportedContainersAt);
      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      if (reportedAt < oneHourAgo) return 'outdated';
    }
    if (sr.reportedContainers == null) return 'unknown';
    if (sr.reportedMissingContainers != null && sr.reportedMissingContainers.length > 0) return 'mismatch';
    return 'healthy';
  }

  getOnPremStatusLabel(site: Site): string {
    switch (this.getOnPremNormalizedStatus(site)) {
      case 'healthy':
        return 'Healthy';
      case 'mismatch':
        return 'Mismatch';
      case 'outdated':
        return 'Outdated';
      default:
        return '—';
    }
  }

  getOnPremStatusClass(site: Site): string {
    switch (this.getOnPremNormalizedStatus(site)) {
      case 'healthy':
        return 'deploy-healthy';
      case 'mismatch':
        return 'deploy-mismatch';
      case 'outdated':
        return 'deploy-outdated';
      default:
        return 'deploy-unknown';
    }
  }

  openBulkDesiredDialog(): void {
    const dialogRef = this.dialog.open(BulkSetReleasesDialogComponent, {
      width: '800px',
      data: {
        selectedSites: this.selected.selected,
        allReleases: this.allReleases,
        siteReleasesMap: this.siteReleasesMap,
      },
    });
    dialogRef.afterClosed().subscribe((success = false) => {
      if (success) {
        this.fetchSites();
        this.fetchAllReleases();
        this.selected.clear();
      }
    });
  }

  openBulkAvailableReleasesDialog(): void {
    const dialogRef = this.dialog.open(BulkSetAvailableReleasesDialogComponent, {
      width: '600px',
      data: {
        selectedSites: this.selected.selected,
        allReleases: this.allReleases,
      },
    });
    dialogRef.afterClosed().subscribe((success = false) => {
      if (success) {
        this.fetchSites();
        this.fetchAllReleases();
        this.selected.clear();
      }
    });
  }
}

@Component({
  selector: 'sites-bulk-set-releases',
  template: `<mat-card appearance="outlined">
    <h3>Bulk Set Desired Release for {{ selectedSites.length }} sites</h3>
    <div class="bulk-release-warning" role="alert">
      This will bypass the manual update mode and immediately update to the desired release for all selected sites.
    </div>
    <p>Set desired release for all selected sites at once.</p>

    <mat-form-field appearance="fill" style="width: 100%">
      <mat-label>Desired Release</mat-label>
      <mat-select [(ngModel)]="selectedDesiredReleaseId">
        <mat-option [value]="null">-- None (clear) --</mat-option>
        <mat-option *ngFor="let release of allReleases" [value]="release.id">
          {{ release.version }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <mat-card-actions>
      <button mat-raised-button color="primary" (click)="onSaveClick()" [disabled]="saving">Save</button>
      <button mat-raised-button (click)="onCancelClick()">Cancel</button>
    </mat-card-actions>

    <h4>Selected sites</h4>
    <ul>
      <li *ngFor="let site of selectedSites">{{ site.name }}</li>
    </ul>
  </mat-card>`,
  styles: [
    `
      mat-card {
        padding: 8px 16px;
      }

      .bulk-release-warning {
        padding: 12px 16px;
        border: 1px solid #f9a825;
        border-left-width: 4px;
        border-radius: 4px;
        background: #fff8e1;
        color: #8d6e00;
        font-weight: 500;
      }
    `,
  ],
  standalone: false,
})
export class BulkSetReleasesDialogComponent {
  selectedSites: Site[];
  allReleases: IotEdgeReleaseResponse[];
  selectedDesiredReleaseId: string | null = null;
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<BulkSetReleasesDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      selectedSites: Site[];
      allReleases: IotEdgeReleaseResponse[];
      siteReleasesMap: { [siteId: string]: SiteReleasesResponse };
    },
    private releaseService: IotEdgeReleaseService,
    public snackBar: MatSnackBar
  ) {
    this.selectedSites = data.selectedSites;
    this.allReleases = data.allReleases;
  }

  async onSaveClick(): Promise<void> {
    if (!this.selectedDesiredReleaseId) {
      this.snackBar.open('Please select a desired release', null, { duration: 5000 });
      return;
    }

    this.saving = true;
    try {
      const results = await Promise.allSettled(
        this.selectedSites.map((site) =>
          this.releaseService.setDesiredRelease(site.id, this.selectedDesiredReleaseId).toPromise()
        )
      );

      const failures = results.filter((r) => r.status === 'rejected');

      if (failures.length > 0) {
        this.snackBar.open(`${failures.length} site(s) failed to update`, null, { duration: 10000 });
      } else {
        this.snackBar.open(`Updated desired release for ${this.selectedSites.length} sites`, null, {
          duration: 5000,
        });
        this.dialogRef.close(true);
      }
    } catch (err) {
      this.snackBar.open(`Error: ${err?.message ?? err}`, null, { duration: 10000 });
    }
    this.saving = false;
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}

@Component({
  selector: 'sites-bulk-set-available-dialog',
  template: `<mat-card appearance="outlined">
    <h3>
      Set Available Releases for {{ data.selectedSites.length }} site{{ data.selectedSites.length === 1 ? '' : 's' }}
    </h3>
    <p>Select the newest release to make available. All earlier releases are included automatically.</p>

    <mat-form-field appearance="fill" style="width: 100%">
      <mat-label>Newest Available Release</mat-label>
      <mat-select [(ngModel)]="selectedId">
        <mat-option [value]="null">-- None (clear all) --</mat-option>
        <mat-option *ngFor="let r of data.allReleases" [value]="r.id">
          {{ r.version }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <p *ngIf="selectedId" style="color: #1565c0; font-size: 12px; font-style: italic; margin: 4px 0 12px">
      This and all earlier releases will be included ({{ selectionCount }} total).
    </p>

    <mat-card-actions>
      <button mat-raised-button color="primary" (click)="onSaveClick()" [disabled]="saving">Save</button>
      <button mat-raised-button (click)="dialogRef.close()">Cancel</button>
    </mat-card-actions>

    <h4>Selected sites</h4>
    <ul>
      <li *ngFor="let site of data.selectedSites">{{ site.name }}</li>
    </ul>
  </mat-card>`,
  standalone: false,
})
export class BulkSetAvailableReleasesDialogComponent {
  selectedId: string | null;
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<BulkSetAvailableReleasesDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { selectedSites: Site[]; allReleases: IotEdgeReleaseResponse[] },
    private releaseService: IotEdgeReleaseService,
    public snackBar: MatSnackBar
  ) {
    this.selectedId = data.allReleases.length > 0 ? data.allReleases[0].id : null;
  }

  get selectionCount(): number {
    if (!this.selectedId) return 0;
    const idx = this.data.allReleases.findIndex((r) => r.id === this.selectedId);
    return idx >= 0 ? this.data.allReleases.length - idx : 0;
  }

  async onSaveClick(): Promise<void> {
    const selectedIndex = this.data.allReleases.findIndex((r) => r.id === this.selectedId);
    const ids = selectedIndex >= 0 ? this.data.allReleases.slice(selectedIndex).map((r) => r.id) : [];

    this.saving = true;
    try {
      const results = await Promise.allSettled(
        this.data.selectedSites.map((site) => this.releaseService.setAvailableReleases(site.id, ids).toPromise())
      );
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        this.snackBar.open(`${failures.length} site(s) failed to update`, null, { duration: 10000 });
      } else {
        this.snackBar.open(`Updated available releases for ${this.data.selectedSites.length} sites`, null, {
          duration: 5000,
        });
        this.dialogRef.close(true);
      }
    } catch (err) {
      this.snackBar.open(`Error: ${err?.message ?? err}`, null, { duration: 10000 });
    }
    this.saving = false;
  }
}
